import { useReducer, useEffect } from 'react';
import useConstant from './useConstant';

/**
 * A very simple state machine hook.
 *
 * @param {object} definition
 * @example
 * const [machine, send] = useStateMachine({
 *   initial: 'idle',
 *   states: {
 *     idle: {
 *       on: { FETCH: 'pending' },
 *       effect: () => {
 *         console.log('entering idle');
 *         return () => console.log('leaving idle');
 *       }
 *     },
 *     pending: {
 *       on: { RESOLVE: 'resolved', REJECT: 'rejected' },
 *     },
 *     resolved: {},
 *     rejected: {},
 *   }
 * })
 */
const useStateMachine = (definition) => {
  const initialState = {
    value: definition.initial,
    context: definition.context || {},
    event: { type: 'INIT' }
  };

  const reducer = (state, event) => {
    if (event.type === 'SET_CONTEXT') {
      return {
        ...state,
        context: event.updater(state.context)
      };
    }
    if (event.type === 'SEND') {
      const resolvedState = definition.states[state.value];
      const resolvedTransition = resolvedState.on?.[event.event];

      // If the transition is not defined keep the current state
      if (!resolvedTransition) return state;

      // Next check if there is a guard on this transition
      const nextState =
        typeof resolvedTransition === 'string'
          ? resolvedTransition
          : resolvedTransition.target;

      if (!nextState) return state;

      const guard = resolvedTransition.guard;

      // If the guard returns false keep the current state
      if (
        typeof guard === 'function' &&
        !guard({
          context: state.context,
          event
        })
      ) {
        return state;
      }

      return {
        ...state,
        value: nextState,
        event
      };
    }
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const send = useConstant(() => (event, payload) => {
    // console.log('SEND', event);
    dispatch({ type: 'SEND', event, payload });
  });

  const setContext = (updater) => {
    dispatch({ type: 'SET_CONTEXT', updater });
  };

  useEffect(() => {
    const onEnter = definition.states[state.value].effect;
    const onExit = onEnter?.({
      send,
      setContext
    });

    return typeof onExit === 'function'
      ? () => onExit({ send, setContext })
      : undefined;
    // We actually only care about a change in the state.value to trigger
    // our effects. It's safe to ignore send and definition here, as these
    // won't like change and could potentially trigger the unintended
    // re-run of an effect if they should change.
    // eslint-disable-next-line
  }, [state.value]);

  return [state, send];
};

export default useStateMachine;
